import { useEffect, useState } from "react";
import { GeneralCommonHeader } from "../common/GeneralCommonHeader";
import { Link } from "react-router-dom";
import {
  DISPLAY_HEADER_ICON_CLOSE,
  DISPLAY_PAGE_HOW_TO_FIX,
} from "../../constants/common_constants";
import { calcUtcDatetime } from "../../utils/basis_util";
import { EvaluateManual } from "../../features/components/howtofix/ui_elements/EvaluateManual";
import { useParams } from "react-router-dom";
import { useApi, useErrorDialog, useInitUsageLog } from "../../hooks";
import { useTranslation } from "react-i18next";
import { FavoriteManual } from "../../features/components/howtofix/ui_elements/FavoriteManual";
import { IFavoriteType } from "../../features/components/howtofix/types/howtofix_types";
import {
  getFavoriteInfo,
  updateFavoriteDisplayDateTime,
} from "../../features/api/dts_api_wrapper";

export const HowToFix = () => {
  //言語切替用
  const { t } = useTranslation();
  //API用
  const { handleApi } = useApi();

  const userId =
    localStorage.getItem(
      `CognitoIdentityServiceProvider.${process.env.REACT_APP_AWS_USER_POOLS_WEB_CLIENT_ID}.LastAuthUser`,
    ) || "";
  type Params = {
    keyId?: string;
  };
  const { keyId } = useParams<Params>(); //パスパラメータで渡されたkeyID(ローカルストレージを参照するのに使用)

  const displayPage = DISPLAY_PAGE_HOW_TO_FIX;
  const displayDateTime = calcUtcDatetime(); //現在日時(UTC時刻)
  const [manualLink, setManualLink] = useState("");
  const cfManualDomain = process.env.REACT_APP_AWS_CF_MANUAL_DOMAIN ?? "";
  const reference = localStorage.getItem(`reference_${keyId}`) ?? "";
  //お気に入り情報を格納
  const [favoriteInfo, setFavoriteInfo] = useState<IFavoriteType>({
    id: 0,
    model: "",
    title: "",
    displayDateTime: "",
  });
  //お気に入り情報かどうかを格納
  const [isFavorite, setIsFavorite] = useState<boolean>(false);

  // 初期表示時の利用ログ登録
  const { handleInit: initUsageLog } = useInitUsageLog(
    {
      userId,
      displayPage,
      displayDateTime,
    },
    false, //エラー時にタブを閉じる
  );

  // エラーダイアログ
  const { showErrorDialog } = useErrorDialog();

  useEffect(() => {
    handleHowToFixPageInit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  //ページ表示時に行うHowToFixの処理
  const handleHowToFixPageInit = async () => {
    try {
      console.log("handleHowToFixPageInit start.");
      if (!userId) {
        //何らかの理由でuserIdが読み込めなかった
        throw new Error("userId is unknown");
      }
      if (!cfManualDomain) {
        throw new Error("manual domain is unknown");
      }
      if (!reference) {
        throw new Error("reference is unknown");
      }
      //マニュアルの参照先をYDSに変換
      const generatedLink = reference.replace(
        "/MaintenanceInformation/index.html#",
        "https://yds-nextc.yanmar.com/static/MaintenanceInformation/index.html#",
      );
      setManualLink(generatedLink);
      console.log("generated: ", generatedLink);

      //利用ログ登録(エラーの場合、タブが閉じる)
      const isError = await initUsageLog();
      if (isError) {
        return;
      }
      //お気に入り情報取得
      const favoriteInfo = await handleGetFavoriteInfo();
      //お気に入り情報であるかどうかを判別, 格納
      const defaultIsFavorite: boolean =
        !!favoriteInfo.id ||
        !!favoriteInfo.model ||
        !!favoriteInfo.title ||
        !!favoriteInfo.displayDateTime;
      setIsFavorite(defaultIsFavorite);
      //お気に入り閲覧日時更新
      if (defaultIsFavorite)
        await handleUpdateFavoriteDisplayDateTime(favoriteInfo.id);
    } catch (err) {
      console.log(`[${displayPage}]err: `, err);
      showErrorDialog(
        t("共通エラーポップ.エラー"),
        t("共通エラーポップ.通信エラーが発生しました。"),
        "closeTab",
      ); //エラー", "通信エラーが発生しました。(タブを閉じる)
    }
  };

  //お気に入り情報取得の処理(呼び出し元でのエラー処理必須)
  const handleGetFavoriteInfo = async () => {
    //お気に入り情報取得
    const favoriteInfoResult = await handleApi(
      getFavoriteInfo,
      userId,
      reference,
    );
    if ("errorMessage" in favoriteInfoResult) {
      //お気に入り情報が取得できなかった場合, throw
      console.log("errorMessage: ", favoriteInfoResult.errorMessage);
      throw new Error("failed to get favorite info");
    }
    //取得したお気に入り情報をstateに保存
    const favoriteInfoStr = JSON.stringify(favoriteInfoResult);
    const favoriteInfoJson: IFavoriteType = JSON.parse(favoriteInfoStr);
    setFavoriteInfo(favoriteInfoJson);

    //お気に入り情報を返す
    return favoriteInfoJson;
  };

  //お気に入り閲覧日時更新の処理(呼び出し元でのエラー処理必須)
  const handleUpdateFavoriteDisplayDateTime = async (id: number) => {
    //お気に入り閲覧日時更新
    const favoriteDisplayDateTimeResult = await handleApi(
      updateFavoriteDisplayDateTime,
      userId,
      id,
    );
    if ("errorMessage" in favoriteDisplayDateTimeResult) {
      //お気に入り閲覧日時更新できなかった場合, throw
      console.log("errorMessage: ", favoriteDisplayDateTimeResult.errorMessage);
      throw new Error("failed to update favorite displayDateTime");
    }
  };

  return (
    <div className="flex flex-col h-svh overflow-hidden">
      {/* ヘッダー */}
      <GeneralCommonHeader
        title="How To Fix"
        displayheaderIcon={DISPLAY_HEADER_ICON_CLOSE}
        onBtnClick={() => window.close()}
      />

      {/* 画面 */}
      <div className="flex flex-col m-2 h-full overflow-hidden">
        <div className="border border-black grow">
          {/* マニュアル情報表示 */}
          <iframe
            src={manualLink}
            title="manual"
            width="100%"
            height="100%"
          ></iframe>
        </div>
        <Link
          to={manualLink}
          target="_blank"
          className="flex justify-end text-sub-blue underline"
        >
          {t("How To Fix画面.マニュアルのみ表示")}
        </Link>
        <div className="flex justify-between">
          <div className="block">
            <p className="whitespace-nowrap">
              {t("How To Fix画面.このページは理解できましたか？")}
              {/* このページは理解できましたか？ */}
            </p>
            <div className="mt-2">
              {/* マニュアル評価(Good/Bad) */}
              <EvaluateManual
                userId={userId}
                displayPage={displayPage}
                reference={reference}
              />
            </div>
          </div>
          <div className="">
            <div className="flex mr-1 mt-2">
              {t("How To Fix画面.お気に入り")}
            </div>
            <div className="flex items-end justify-center">
              <FavoriteManual
                userId={userId}
                reference={reference}
                favoriteInfo={favoriteInfo}
                isFavorite={isFavorite}
                setIsFavorite={setIsFavorite}
                setFavoriteInfo={setFavoriteInfo}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
